/**
 * Field Utilities
 *
 * Functions for normalizing field values during inline/mass updates.
 * Handles extraction of UUIDs from full objects, array normalization, etc.
 */

import { FieldCategory, FieldConfig } from '../types/EntityConfig'

/**
 * Field categories that store arrays of UUIDs
 */
const ARRAY_UUID_CATEGORIES: FieldCategory[] = ['team_role', 'contact', 'org_reference']

/**
 * Field categories that are always arrays
 */
const ARRAY_CATEGORIES: FieldCategory[] = ['team_role', 'contact', 'org_reference', 'multiselect']

/**
 * UUID field names by category
 */
const UUID_FIELD_MAP: Record<string, string> = {
  team_role: 'user_uuid',
  contact: 'contacts_uuid',
  org_reference: 'org_uuid',
  user_reference: 'user_uuid',
}

/**
 * Normalize a field value for storage based on its category
 *
 * @param field - Field name
 * @param value - Raw value from the edit cell
 * @param fieldConfig - Optional field configuration for custom handling
 * @returns Normalized value suitable for storage
 */
export const normalizeFieldValue = (
  field: string,
  value: any,
  fieldConfig?: FieldConfig
): any => {
  const category = fieldConfig?.category || inferCategory(field)
  const uuidField = fieldConfig?.uuidField || UUID_FIELD_MAP[category] || 'id'

  // Handle null/undefined
  if (value === null || value === undefined) {
    return ARRAY_CATEGORIES.includes(category) ? [] : value
  }

  // Team role fields (team.AM, team.PM, etc.) - extract user_uuids
  if (category === 'team_role') {
    return extractUuids(value, uuidField)
  }

  // Contact fields - extract contacts_uuids
  if (category === 'contact') {
    return extractUuids(value, uuidField)
  }

  // Org relationship fields - extract org_uuids
  if (category === 'org_reference') {
    return extractUuids(value, uuidField)
  }

  // User reference (single user) - extract user_uuid
  if (category === 'user_reference') {
    if (Array.isArray(value)) {
      const first = value[0]
      if (!first) return null
      if (typeof first === 'string') return first
      return first[uuidField] || first.id || first
    }
    if (typeof value === 'string') return value
    return value[uuidField] || value.id || value
  }

  // Multiselect - keep as array
  if (category === 'multiselect') {
    if (!Array.isArray(value)) {
      return value !== undefined && value !== null ? [value] : []
    }
    return value
  }

  // Select - flatten single-item arrays
  if (category === 'select') {
    if (Array.isArray(value) && value.length === 1) {
      return value[0]
    }
    return value
  }

  // Default: return as-is
  return value
}

/**
 * Extract UUIDs from an array of objects or strings
 */
const extractUuids = (value: any, uuidField: string): string[] => {
  if (!Array.isArray(value)) {
    if (typeof value === 'string') return [value]
    if (value && value[uuidField]) return [value[uuidField]]
    return []
  }

  return value.map((item: any) => {
    if (typeof item === 'string') return item
    if (item && item[uuidField]) return item[uuidField]
    return item
  }).filter(Boolean)
}

/**
 * Infer field category from field name patterns
 */
const inferCategory = (field: string): FieldCategory => {
  // Team role pattern
  if (field.startsWith('team.')) return 'team_role'

  // Known contact fields
  if (['primaryContact', 'billingContact', 'contacts'].includes(field)) return 'contact'

  // Known org relationship fields
  if (['contractedBy', 'affiliated'].includes(field)) return 'org_reference'

  // Owner is a user reference
  if (field === 'owner') return 'user_reference'

  // Default to text
  return 'text'
}

/**
 * Get value from object - supports both literal keys ('team.AM') and nested paths
 */
export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  if (!obj) return undefined

  // First try literal key (e.g., obj['team.AM'])
  if (path in obj) {
    return obj[path]
  }

  // Then try nested path (e.g., obj.team.AM)
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Set a nested value on an object
 */
export const setNestedValue = (obj: Record<string, any>, path: string, value: any): Record<string, any> => {
  const result = { ...obj }
  const keys = path.split('.')

  if (keys.length === 1) {
    result[path] = value
    return result
  }

  let current: any = result
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    current[key] = current[key] ? { ...current[key] } : {}
    current = current[key]
  }
  current[keys[keys.length - 1]] = value

  return result
}

/**
 * Deep equality check for values
 */
export const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== typeof b) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, idx) => isEqual(item, b[idx]))
  }

  if (typeof a === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    return keysA.every(key => isEqual(a[key], b[key]))
  }

  return false
}

/**
 * Detect which fields changed between old and new row
 */
export const getChangedFields = <T extends Record<string, any>>(
  newRow: T,
  oldRow: T,
  editableFields: string[]
): Record<string, any> => {
  const changes: Record<string, any> = {}

  for (const field of editableFields) {
    const newValue = getNestedValue(newRow, field)
    const oldValue = getNestedValue(oldRow, field)

    if (!isEqual(newValue, oldValue)) {
      changes[field] = newValue
    }
  }

  return changes
}

/**
 * Check if a value is valid for update (not empty)
 */
export const isValidValue = (value: any): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return true // Allow empty arrays for clearing
  if (typeof value === 'boolean') return true
  if (typeof value === 'number') return true
  return false
}
